<script setup>
import { computed } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import moment from 'moment'
import { buildProfileUrl, getFullUrl, displayPhone } from '@/plugins/misc'
import MemberVerifiedTags from '@core/components/MemberVerifiedTags.vue'

const props = defineProps({
  member: { type: Object, default: null },
  loading: { type: Boolean, default: false },
})

const renewalDate = computed(() => {
  if (!props.member?.subscriptions?.length) return null
  const active = props.member.subscriptions.find(s => s.status === 'active' || s.status === 'trialing')

  return active?.paidThroughDate ? moment(active.paidThroughDate).format('MMM D, YYYY') : null
})

const memberSince = computed(() => {
  if (!props.member?.memberSince) return null

  return moment(props.member.memberSince).format('MMM D, YYYY')
})

const dateJoined = computed(() => {
  if (!props.member?.createdAt) return null

  return moment(props.member.createdAt).format('MMM D, YYYY')
})
</script>

<template>
  <div class="support-member-context">
    <div
      v-if="loading"
      class="d-flex justify-center pa-6"
    >
      <VProgressCircular
        indeterminate
        color="primary"
        size="32"
      />
    </div>

    <template v-else-if="member">
      <!-- Profile card matching Profile.vue layout -->
      <div class="profile-container content-card">
        <div class="profile-header">
          <div class="flex flex-column align-center">
            <div class="avatar rounded-circle">
              <VImg
                v-if="member.profileImageId"
                class="avatar-image"
                :src="mediaUrl(member.profileImageId, 'small')"
                cover
              />
              <div
                v-else
                class="avatar-fallback flex flex-row align-center justify-center"
              >
                <span class="avatar-fallback-text">{{ member.firstName?.[0]?.toUpperCase() }}{{ member.lastName?.[0]?.toUpperCase() }}</span>
              </div>
            </div>
          </div>
          <div class="profile-header-details">
            <div
              class="text-md font-weight-semibold"
              style="line-height: 1.2em;"
            >
              <a
                v-if="member.profileId"
                :href="`/m/${member.profileId}`"
                target="_blank"
                style="text-decoration: inherit; color: inherit;"
              >
                {{ member.fullName || `${member.firstName} ${member.lastName}` }}
                <VIcon
                  v-if="member.membershipType === 'platinum' && member.verified"
                  size="20"
                  class="verified-icon"
                  style="margin: 0px; margin-top: -4px;"
                >
                  material-symbols:verified
                </VIcon>
                <span
                  v-if="member.membershipType && member.membershipType !== 'standard'"
                  class="text-sm mt-1"
                >
                  ({{ member.membershipType === 'trial' ? 'Platinum' : member.membershipType }})
                </span>
              </a>
              <template v-else>
                {{ member.fullName || `${member.firstName} ${member.lastName}` }}
              </template>
            </div>

            <div
              v-if="member.title && member.company"
              class="text-sm mt-1"
            >
              {{ member.title }} - {{ member.company }}
            </div>
            <div
              v-else-if="member.company"
              class="text-sm mt-1"
            >
              {{ member.company }}
            </div>
            <div
              v-if="member.title2 && member.company2"
              class="text-sm"
            >
              {{ member.title2 }} - {{ member.company2 }}
            </div>
            <div
              v-else-if="member.company2"
              class="text-sm"
            >
              {{ member.company2 }}
            </div>
          </div>
        </div>

        <!-- Verified tags -->
        <span
          v-if="member.membershipType === 'platinum' && member.verifiedTags?.length"
          class="mt-1 mb-1"
        >
          <MemberVerifiedTags :member="member" />
        </span>

        <!-- Bio -->
        <div
          v-if="member.bio"
          class="text-sm user-bio mt-4"
        >
          {{ member.bio }}
        </div>

        <!-- Stats -->
        <div class="profile-header-stats d-flex align-center mt-2 mb-1">
          <div class="d-flex align-center gap-1">
            <span class="font-weight-semibold">{{ member.favoritesCount || '-' }}</span>
            <span class="text-xs">Favorites</span>
          </div>
          <span style="margin: 0px 4px; color: rgb(153, 153, 153);" />
          <div class="d-flex align-center px-1 gap-1">
            <span class="font-weight-semibold">{{ member.friendsCount || '-' }}</span>
            <span class="text-xs">Connections</span>
          </div>
        </div>

        <!-- Social links -->
        <div class="mt-0 pt-0">
          <div class="d-flex align-center pt-1">
            <a
              v-if="member.profileLinkedInProfileId"
              :href="buildProfileUrl('linkedin', member.profileLinkedInProfileId)"
              target="_blank"
            >
              <VIcon
                size="20"
                class="mr-3"
              >
                mdi-linkedin
              </VIcon>
            </a>
            <a
              v-if="member.profileInstagramProfileId"
              :href="buildProfileUrl('instagram', member.profileInstagramProfileId)"
              target="_blank"
            >
              <VIcon
                size="20"
                class="mr-3"
              >
                mdi-instagram
              </VIcon>
            </a>
            <a
              v-if="member.profileXProfileId"
              :href="buildProfileUrl('twitter', member.profileXProfileId)"
              target="_blank"
            >
              <VIcon
                size="20"
                class="mr-3"
              >
                simple-icons:x
              </VIcon>
            </a>
            <a
              v-if="member.profileTikTokProfileId"
              :href="buildProfileUrl('tiktok', member.profileTikTokProfileId)"
              target="_blank"
            >
              <VIcon
                size="20"
                class="mr-3"
              >
                simple-icons:tiktok
              </VIcon>
            </a>
            <a
              v-if="member.profileWebsiteLink"
              :href="getFullUrl(member.profileWebsiteLink)"
              target="_blank"
            >
              <VIcon
                size="20"
                class="mr-3"
              >
                mdi-web
              </VIcon>
            </a>
          </div>
        </div>

        <!-- Contact & Dates -->
        <VDivider class="my-3" />
        <div class="context-details">
          <div
            v-if="member.email"
            class="context-row"
          >
            <span class="context-label">Email</span>
            <span class="context-value">{{ member.email }}</span>
          </div>
          <div
            v-if="member.phoneNumber"
            class="context-row"
          >
            <span class="context-label">Phone</span>
            <span class="context-value">{{ displayPhone(member.phoneNumber) }}</span>
          </div>
          <div
            v-if="memberSince"
            class="context-row"
          >
            <span class="context-label">Member Since</span>
            <span class="context-value">{{ memberSince }}</span>
          </div>
          <div
            v-if="dateJoined"
            class="context-row"
          >
            <span class="context-label">Date Joined</span>
            <span class="context-value">{{ dateJoined }}</span>
          </div>
          <div
            v-if="renewalDate"
            class="context-row"
          >
            <span class="context-label">Renewal Date</span>
            <span class="context-value">{{ renewalDate }}</span>
          </div>
        </div>

        <!-- View profile link -->
        <VBtn
          v-if="member.profileId"
          :href="`/m/${member.profileId}`"
          target="_blank"
          variant="text"
          size="small"
          density="compact"
          class="mt-3"
        >
          View Full Profile
        </VBtn>
      </div>
    </template>

    <div
      v-else
      class="pa-6 text-center text-medium-emphasis"
    >
      Select a conversation to view member details
    </div>
  </div>
</template>

<style scoped>
.support-member-context {
  height: 100%;
  overflow-y: auto;
}

.profile-container {
  display: flex;
  flex-direction: column;
  align-items: start;
  max-width: 460px;
  margin: 0 auto;
  border: thin solid var(--ds-border-light);
  border-radius: var(--ds-radius-sm);
  padding: 0.5rem;
  margin-top: 0.5rem;
}

.profile-header {
  width: 100%;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.profile-header-details {
  flex: 1;
  min-width: 0;
  margin-top: 20px;
}

.avatar {
  width: 6rem;
  height: 6rem;
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 0.25rem;
  margin: 0.3rem;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  font-size: 1.6rem;
}

.avatar-image {
  border-radius: var(--ds-radius-full);
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-fallback-text {
  width: fit-content;
}

.profile-header-stats {
  gap: 0.4rem;
}

.verified-icon {
  color: rgb(0, 149, 246);
  width: 1.25rem;
  height: 1.25rem;
  margin-bottom: 5px;
}

.user-bio {
  max-width: 100ch;
}

.context-details {
  width: 100%;
}

.context-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: var(--ds-space-1) 0;
}

.context-label {
  font-size: var(--ds-text-xs);
  color: var(--ds-text-muted);
  flex-shrink: 0;
  margin-right: var(--ds-space-2);
}

.context-value {
  font-size: var(--ds-text-sm);
  text-align: right;
  word-break: break-word;
}
</style>
